import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { AccountInfo } from "@azure/msal-browser";
import { loginRequest, apiRequest } from '../authConfig';
import { clearTelemetryUserContext, setTelemetryUserContext } from '../services/telemetry';

interface AuthContextType {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  getIdToken: () => string | null;
  refreshAccessToken: () => Promise<string | null>;
  loading: boolean;
  idToken: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [loading, setLoading] = useState(true);
  const [activeAccount, setActiveAccount] = useState<AccountInfo | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [expiresOn, setExpiresOn] = useState<Date | null>(null);
  const refreshTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLoading(inProgress === "startup" || inProgress === "handleRedirect");
  }, [inProgress]);

  useEffect(() => {
    if (accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
      setActiveAccount(accounts[0]);
      if (accounts[0].idToken) {
        setIdToken(accounts[0].idToken);
      }
    } else if (instance.getActiveAccount()) {
      const account = instance.getActiveAccount()!;
      setActiveAccount(account);
      if (account.idToken) {
        setIdToken(account.idToken);
      }
    }
  }, [accounts, instance]);

  useEffect(() => {
    const claims = activeAccount?.idTokenClaims as Record<string, unknown> | undefined;
    const userId =
      (typeof claims?.sub === 'string' ? claims.sub : undefined) ||
      (typeof claims?.oid === 'string' ? claims.oid : undefined) ||
      activeAccount?.localAccountId ||
      activeAccount?.homeAccountId;
    if (userId) {
      setTelemetryUserContext(userId, activeAccount?.homeAccountId);
      return;
    }

    clearTelemetryUserContext();
  }, [activeAccount]);

  useEffect(() => {
    return () => {
      if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
    };
  }, []);

  // Helper to decode JWT expiry (if MSAL doesn't provide it)
  const decodeJwtExpiry = (token: string): number | null => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : null;
    } catch (e) {
      console.error("[AuthProvider] Failed to decode JWT expiry:", e);
      return null;
    }
  };

  // Force refresh token, defined before useEffect
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    if (!activeAccount) {
      console.warn("[AuthProvider] refreshAccessToken called but no activeAccount.");
      return null;
    }
    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: activeAccount,
        forceRefresh: true,
      });
      setAccessToken(response.accessToken);
      setIdToken(response.idToken)
      setExpiresOn(
        response.expiresOn ??
        (response.accessToken ? new Date(decodeJwtExpiry(response.accessToken) || 0) : null)
      );
      return response.accessToken;
    } catch (error) {
      console.warn("[AuthProvider] Silent token refresh failed, falling back to popup:", error);
      try {
        const response = await instance.acquireTokenPopup({
          ...apiRequest,
          account: activeAccount,
        });
        setAccessToken(response.accessToken);
        setExpiresOn(
          response.expiresOn ??
          (response.accessToken ? new Date(decodeJwtExpiry(response.accessToken) || 0) : null)
        );
        return response.accessToken;
      } catch (interactiveError) {
        console.error("[AuthProvider] Token refresh failed (popup):", interactiveError);
        return null;
      }
    }
  }, [activeAccount, instance]);

  // Automated refresh effect (MUST come after refreshAccessToken definition)
  useEffect(() => {
    if (!accessToken || !expiresOn) {
      return;
    }
    if (refreshTimeout.current) {
      clearTimeout(refreshTimeout.current);
    }

    // For testing, refresh every 5 seconds
    const msBeforeExpiry = expiresOn.getTime() - Date.now() - (2 * 60 * 1000);

    if (msBeforeExpiry > 0) {
      refreshTimeout.current = setTimeout(() => {
        console.log(
          `[AuthProvider] Automatically refreshing access token. Expires at ${expiresOn.toISOString()}. Refreshing now.`
        );
        refreshAccessToken();
      }, msBeforeExpiry);
    }
  }, [accessToken, expiresOn, refreshAccessToken]);

  const login = useCallback(async () => {
    try {
      const response = await instance.loginPopup(loginRequest);
      console.log("[AuthProvider] loginPopup successful:", response);
      instance.setActiveAccount(response.account);
      setIdToken(response.idToken);
      setActiveAccount(response.account);
    } catch (error) {
      console.warn("[AuthProvider] loginPopup failed, trying redirect:", error);
      try {
        await instance.loginRedirect(loginRequest);
        console.log("[AuthProvider] loginRedirect successful.");
      } catch (redirectError) {
        console.error("[AuthProvider] loginRedirect failed:", redirectError);
        throw redirectError;
      }
    }
  }, [instance]);

  const logout = useCallback(() => {
    clearTelemetryUserContext();
    instance.logoutPopup({
      postLogoutRedirectUri: globalThis.window.location.origin,
    });
  }, [instance]);

  // Get access token, refresh if needed
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!activeAccount) {
      console.warn("[AuthProvider] getAccessToken called but no activeAccount.");
      return null;
    }
    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: activeAccount,
      });
      setAccessToken(response.accessToken);
      setExpiresOn(
        response.expiresOn ??
        (response.accessToken ? new Date(decodeJwtExpiry(response.accessToken) || 0) : null)
      );
      console.log("[AuthProvider] acquireTokenSilent successful.");
      return response.accessToken;
    } catch (error) {
      console.warn("[AuthProvider] acquireTokenSilent failed, trying acquireTokenPopup:", error);
      try {
        const response = await instance.acquireTokenPopup({
          ...apiRequest,
          account: activeAccount,
        });
        setAccessToken(response.accessToken);
        setExpiresOn(
          response.expiresOn ??
          (response.accessToken ? new Date(decodeJwtExpiry(response.accessToken) || 0) : null)
        );
        return response.accessToken;
      } catch (interactiveError) {
        console.error("[AuthProvider] acquireTokenPopup failed:", interactiveError);
        return null;
      }
    }
  }, [activeAccount, instance]);

  const getIdToken = useCallback((): string | null => {
    if (idToken) {
      return idToken;
    }
    if (activeAccount?.idToken) {
      return activeAccount.idToken;
    }
    return null;
  }, [activeAccount?.idToken, idToken]);

  useEffect(() => {
  if (activeAccount) {
    getAccessToken();
  }
}, [activeAccount, getAccessToken]);

  const value = useMemo<AuthContextType>(() => ({
    isAuthenticated,
    user: activeAccount,
    login,
    logout,
    getAccessToken,
    getIdToken,
    refreshAccessToken,
    loading,
    idToken,
  }), [
    isAuthenticated,
    activeAccount,
    login,
    logout,
    getAccessToken,
    getIdToken,
    refreshAccessToken,
    loading,
    idToken,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};